/**
 * 화면이 실제로 내보내는 그림의 꼴 — 순수 계산.
 *
 * 모니터링 벽이 카드를 전부 16:9 로 그리던 시절에는, 세로로 세운 패널도 가로 상자에
 * 담겨 나왔다. 운영자가 벽 앞에서 답해야 하는 질문 중 하나가 **"저 화면 왜 저렇게
 * 나오지"** 인데, 그 답의 절반(화면이 세로다)을 벽 자신이 감추고 있었던 셈이다.
 *
 * 계산을 컴포넌트에서 떼어 낸 이유는 여기가 틀리기 쉬운 자리이기 때문이다. 아래
 * "어느 값을 믿는가" 를 한 번 잘못 정하면 **한 플랫폼의 모든 세로 화면이 가로로**
 * 보이고, 비를 이름으로 바꾸는 자리에서 오차를 안 주면 1366×768 이 16:9 와 다른
 * 묶음으로 떨어져 나간다. 묶어 보라고 만든 화면이 오히려 흩어 놓게 된다.
 */

/**
 * **어느 값을 믿는가 — 이 파일에서 가장 중요한 결정.**
 *
 * `screen_w`·`screen_h` 는 화면이 보고한 값인데, **그 뜻이 셸마다 다르다.**
 *
 *   - 안드로이드는 OS 에 회전을 요청한다(`setRequestedOrientation`). 그래서
 *     `window.screen` 이 **회전을 마친** 축을 보고한다 — 세로로 세운 패널은 1080×1920.
 *   - Tauri·웹은 CSS 로만 돌린다. `window.screen` 은 **물리 축 그대로**다 —
 *     같은 패널이 1920×1080 으로 보고된다.
 *
 * 그리고 셸 종류는 API 에 나오지 않는다(`PlayerDevice.platform` 은 서버 안에만 있다).
 * 그래서 "회전이 90·270 이면 가로세로를 바꾼다" 같은 규칙은 **한쪽 플랫폼에서 반드시
 * 거꾸로 틀린다.**
 *
 * 대신 두 값을 갈라 쓴다. 각각 어긋남에 강한 쪽에서만 가져온다.
 *
 *   - **비(16:9 인가 4:3 인가)** 는 보고된 픽셀의 `긴 변 : 짧은 변` 에서 가져온다.
 *     max·min 은 두 값이 뒤바뀌어도 그대로라, 셸이 어느 쪽으로 보고하든 같은 답이 난다.
 *   - **방향(가로인가 세로인가)** 은 `orientation` 에서 가져온다. 서버가 `rotation`
 *     에서 파생시키는 값이고(90·270 이면 portrait), 회전각은 셸과 무관하게 같은 뜻이다.
 *
 * 둘을 합치면 두 플랫폼에서 같은 답이 난다. 알 수 있는 것만으로 답을 만든 것이고,
 * 여기에 더 얹으려면 서버가 `platform` 을 먼저 내려줘야 한다.
 */

/** 보는 사람 눈에 보이는 방향. */
export type OutputOrientation = 'landscape' | 'portrait' | 'square';

export interface FormatInput {
  /** 콘솔에 설정된 방향. 서버가 `rotation` 에서 파생시킨 값이다. */
  orientation: 'landscape' | 'portrait';
  /** 화면이 보고한 해상도. 보고 전이면 null. 축의 뜻은 셸마다 다르다(위 설명). */
  screenW: number | null;
  screenH: number | null;
}

export interface OutputFormat {
  /** 눈에 보이는 가로·세로 픽셀. 방향에 맞춰 세운 값이다. 보고가 없으면 null. */
  width: number | null;
  height: number | null;
  /** 가로/세로 비. 그릴 때 `aspect-ratio` 에 그대로 넣는다. */
  ratio: number;
  orientation: OutputOrientation;
  /** 해상도를 화면이 보고했는가. false 면 비는 **설정에서 추정한 것**이다. */
  reported: boolean;
  /** "16:9" 처럼 사람이 읽는 비. */
  ratioLabel: string;
  /** 같은 꼴이면 같은 값. 묶을 때 쓴다. */
  key: string;
  /** 묶음 머리에 쓰는 이름. "세로 9:16" */
  groupLabel: string;
  /** 카드에 붙이는 한 줄. "1080×1920 · 세로 9:16" */
  label: string;
}

/**
 * 이름이 있는 비.
 *
 * 가로로만 적어 두고 세로는 뒤집어 쓴다 — 같은 표를 두 번 적으면 반드시 한쪽만 고친다.
 */
const NAMED_RATIOS: ReadonlyArray<{ label: string; value: number }> = [
  { label: '32:9', value: 32 / 9 },
  { label: '21:9', value: 21 / 9 },
  { label: '16:9', value: 16 / 9 },
  { label: '16:10', value: 16 / 10 },
  { label: '3:2', value: 3 / 2 },
  { label: '4:3', value: 4 / 3 },
  { label: '5:4', value: 5 / 4 },
];

/**
 * 이름에 붙일 때 봐 주는 오차.
 *
 * **파는 이름과 실제 픽셀이 다르다.** 1366×768 은 정확히는 16:9 가 아니고(1.7786 대
 * 1.7778), "21:9" 로 팔리는 울트라와이드는 아예 21:9 인 것이 없다 — 2560×1080 은
 * 64:27(2.370), 3440×1440 은 2.389 다. 딱 맞는 값만 이름으로 바꾸면 같은 꼴로 묶여야
 * 할 화면들이 "21:9"·"64:27"·"43:18" 로 갈라져 **묶음만 늘어난다.** 묶어 보라고 만든
 * 화면이 흩어 놓는 셈이다.
 *
 * 2.5% 까지 봐 준다. 이름이 붙은 비끼리는 가장 가까운 이웃도 6% 넘게 떨어져 있어
 * (5:4 와 4:3 이 6.6%, 나머지는 그보다 멀다) 엉뚱한 이름이 붙을 여지는 없다.
 */
const RATIO_TOLERANCE = 0.025;

/** 정사각으로 볼 범위. 1:1 패널은 드물지만 있고, 픽셀이 한둘 어긋나는 일이 흔하다. */
const SQUARE_TOLERANCE = 0.02;

const ORIENTATION_LABEL: Record<OutputOrientation, string> = {
  landscape: '가로',
  portrait: '세로',
  square: '정사각',
};

function gcd(a: number, b: number): number {
  return b === 0 ? a : gcd(b, a % b);
}

/**
 * 긴 변과 짧은 변의 비를 이름으로.
 *
 * 이름이 없으면 약분해서 적고, 약분해도 숫자가 크면 소수로 적는다. `1237:715` 같은
 * 것은 적어 봐야 아무에게도 아무 뜻이 아니다.
 */
function longSideRatioLabel(long: number, short: number): string {
  const value = long / short;

  const named = NAMED_RATIOS.find((entry) => Math.abs(value - entry.value) / entry.value <= RATIO_TOLERANCE);
  if (named) return named.label;

  const divisor = gcd(long, short);
  const a = long / divisor;
  const b = short / divisor;
  if (a <= 64 && b <= 64) return `${a}:${b}`;

  return `${value.toFixed(2)}:1`;
}

/** 비 이름을 방향에 맞춰 적는다 — 세로 화면은 뒤집어 적어야 세로로 읽힌다. */
function ratioLabelFor(long: number, short: number, orientation: OutputOrientation): string {
  if (orientation === 'square') return '1:1';
  const label = longSideRatioLabel(long, short);
  if (orientation === 'landscape') return label;
  const [a, b] = label.split(':');
  return `${b}:${a}`;
}

/** 이 화면이 지금 내보내고 있는 그림의 꼴. */
export function outputFormat(input: FormatInput): OutputFormat {
  const reportedW = input.screenW;
  const reportedH = input.screenH;
  const facing: 'landscape' | 'portrait' = input.orientation === 'portrait' ? 'portrait' : 'landscape';

  /*
   * 해상도 보고가 없다. 설정된 방향만 알고 비는 모른다.
   *
   * 모르면서 아는 척하지 않는다 — 16:9 로 그리되 그것이 추정이라고 말하고, 묶음도
   * 따로 둔다. 보고한 16:9 화면과 한 칸에 섞이면 "이 묶음은 전부 16:9" 가 거짓이 된다.
   */
  if (reportedW === null || reportedH === null || reportedW <= 0 || reportedH <= 0) {
    return {
      width: null,
      height: null,
      ratio: facing === 'portrait' ? 9 / 16 : 16 / 9,
      orientation: facing,
      reported: false,
      ratioLabel: facing === 'portrait' ? '9:16' : '16:9',
      key: `${facing}|unreported`,
      groupLabel: `${ORIENTATION_LABEL[facing]} · 해상도 미보고`,
      label: `해상도 미보고 · ${ORIENTATION_LABEL[facing]} 설정`,
    };
  }

  /*
   * 긴 변·짧은 변으로 받는다 — 셸이 축을 어느 쪽으로 보고하든 같은 값이 나온다.
   * 이 파일 첫머리의 "어느 값을 믿는가" 가 여기서 실제로 지켜진다.
   */
  const long = Math.max(reportedW, reportedH);
  const short = Math.min(reportedW, reportedH);

  const orientation: OutputOrientation = long / short <= 1 + SQUARE_TOLERANCE ? 'square' : facing;
  const ratioLabel = ratioLabelFor(long, short, orientation);

  // 방향에 맞춰 세운다. 정사각은 어느 쪽으로 적어도 같다.
  const width = orientation === 'portrait' ? short : long;
  const height = orientation === 'portrait' ? long : short;

  return {
    width,
    height,
    ratio: width / height,
    orientation,
    reported: true,
    ratioLabel,
    key: `${orientation}|${ratioLabel}`,
    groupLabel: `${ORIENTATION_LABEL[orientation]} ${ratioLabel}`,
    label: `${width}×${height} · ${ORIENTATION_LABEL[orientation]} ${ratioLabel}`,
  };
}

/**
 * 모니터링 카드 한 장의 꼴.
 *
 * 벽이 들고 있는 카드 꼴에서 바로 뽑아 쓰라고 둔 편의 함수다. 예전에는 벽을 지점으로
 * 묶는 모듈(`wallGroups.ts`)에 있었는데 — 묶음이 같은 계산으로 "이 지점은 전부 세로"를
 * 판정했다 — 묶음을 걷으면서 그 모듈이 통째로 사라졌다. 계산 자체는 꼴의 것이라 여기 남는다.
 */
export function formatOf(card: {
  orientation: 'landscape' | 'portrait';
  status: { screen_w: number | null; screen_h: number | null };
}): OutputFormat {
  return outputFormat({
    orientation: card.orientation,
    screenW: card.status.screen_w,
    screenH: card.status.screen_h,
  });
}
